/**
 * The Job section — `docs/new-request.md` §8, Board 6, Board 15 A.
 *
 * Title and Division (both needed), Lead source (most used first, a new one
 * added on the spot), Budget, and *When they want it done* across the full
 * width. A contact or property that already has jobs starts Lead source at
 * *Repeat customer*, and the hint says why until somebody changes it.
 */
import { useEffect } from 'react'
import { BUDGET_BANDS, type BudgetBand } from '@rowted/shared'
import { Field } from '../components/form/field.js'
import { TextInput } from '../components/form/inputs.js'
import { KeySelect } from './key-select.js'
import { Tile } from '../components/form/icon-picker.js'
import type { RequestSetup } from './api.js'
import { jobStatus, jobSummary, wantedLabel, type JobFields, type SheetState } from './model.js'
import { MissingHint, SectionHeading } from './section-frame.js'
import { useRequestSheet } from './sheet-context.js'
import { PhonePicker } from './job-kit.js'
import { WhenField, wantedToSend } from './when-field.js'

const REPEAT = 'Repeat customer'
const FROM_HISTORY = 'filled from their last job — change it if they heard about you again'

/** The lead source's name, chosen or about to be added. */
export function leadSourceName(job: JobFields, setup: RequestSetup): string | null {
  if (job.newLeadSource) return job.newLeadSource
  return setup.leadSources.find((source) => source.id === job.leadSourceId)?.name ?? null
}

/** *Hardscape · Yard sign · by Wed, Nov 25 · $10k–25k* — the folded line (Board 7). */
export function jobSummaryOf(state: SheetState, setup: RequestSetup): string {
  const job = state.job
  return jobSummary(job, {
    division: setup.divisions.find((division) => division.code === job.divisionCode)?.name ?? null,
    leadSource: leadSourceName(job, setup),
    wanted: wantedLabel(wantedToSend(job), setup.when),
  })
}

/** The chosen contact or property has worked with the company before (§8). */
function hasHistory(state: SheetState): boolean {
  if (state.contact.kind === 'chosen' && state.contact.contact.hasJobs) return true
  return state.property.kind === 'chosen' && state.property.property.jobs > 0
}

/**
 * *Repeat customer* when they have a job and nothing is chosen yet (§8). A
 * pure step, so the sheet can apply it the moment a record is chosen as well
 * as when this section opens.
 */
export function applyLeadSourceDefault(state: SheetState, setup: RequestSetup): SheetState {
  const job = state.job
  if (job.leadSourceId || job.newLeadSource || !hasHistory(state)) return state
  const repeat = setup.leadSources.find((source) => source.name === REPEAT)
  if (!repeat) return state
  return { ...state, job: { ...job, leadSourceId: repeat.id, leadSourceFromHistory: true } }
}

export function JobSection({ mode = 'open' }: { mode?: 'open' | 'card' }) {
  const api = useRequestSheet()
  const { setup, phone } = api
  const job = api.state.job
  const ready = jobStatus(api.state) === 'ready'

  const setJob = (change: Partial<JobFields>) =>
    api.update((state) => ({ ...state, job: { ...state.job, ...change } }))

  /* The default follows a choice made while this section was folded, too. */
  const history = hasHistory(api.state)
  useEffect(() => {
    if (history) api.update((state) => applyLeadSourceDefault(state, setup))
    /* Only when the history appears; the update reads the latest state itself. */
  }, [history])

  /*
   * Done, the job rolls up the way the contact and the place do — its heading
   * and a two-line card with Edit — until scheduling starts (Aaron, step 28).
   */
  if (mode === 'card') {
    const division = setup.divisions.find((one) => one.code === job.divisionCode) ?? null
    return (
      <section aria-label="Job">
        <SectionHeading section="job" ready={ready} />
        <div className="flex items-center gap-3 rounded-[12px] border border-accent-line bg-green-band-bg px-3.5 py-3">
          <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center">
            {division
              ? <Tile set="division" slug={division.icon} colour={division.colour} size={38} />
              : null}
          </span>
          <div className="flex min-w-0 flex-col gap-[3px] leading-tight">
            <b className="truncate text-[14.5px] font-bold text-text">{job.title.trim()}</b>
            <span className="text-[12.5px] text-text-muted">{jobSummaryOf(api.state, setup)}</span>
          </div>
          <span className="flex-1" />
          <button type="button" onClick={() => api.openSection('job')}
            className="h-[30px] shrink-0 rounded-button px-1.5 text-[12.5px] font-semibold text-accent hover:underline">
            Edit
          </button>
        </div>
      </section>
    )
  }

  const titleHint = api.hintFor('title')
  const divisionHint = api.hintFor('division')

  const title = (
    <Field label="Title" required>
      {(ids) => (
        <>
          <TextInput
            id={ids.id}
            value={job.title}
            size={phone ? 'touch' : 'default'}
            invalid={Boolean(titleHint)}
            data-request-field="title"
            onChange={(event) => setJob({ title: event.target.value })}
          />
          <MissingHint text={titleHint} />
        </>
      )}
    </Field>
  )

  const divisionOptions = setup.divisions.map((one) => ({
    value: one.code,
    label: one.name,
    glyph: <Tile set="division" slug={one.icon} colour={one.colour} size={20} />,
  }))
  const divisionField = (
    <Field label="Division" required>
      {(ids) => (
        <>
          {phone
            ? (
              <PhonePicker
                label="Division"
                field="division"
                value={job.divisionCode}
                invalid={Boolean(divisionHint)}
                options={divisionOptions.map((option) => ({
                  value: option.value, label: option.label, lead: option.glyph,
                }))}
                placeholder="Choose a division"
                onChange={(value) => setJob({ divisionCode: value })}
              />
            )
            : (
              <KeySelect
                id={ids.id}
                label="Division"
                field="division"
                value={job.divisionCode}
                invalid={Boolean(divisionHint)}
                placeholder="Choose a division"
                options={divisionOptions.map((option) => ({
                  value: option.value, label: option.label, lead: option.glyph,
                }))}
                onChange={(value) => setJob({ divisionCode: value })}
              />
            )}
          <MissingHint text={divisionHint} />
        </>
      )}
    </Field>
  )

  const leadSource = (
    <Field label="Lead source" hint={job.leadSourceFromHistory ? FROM_HISTORY : undefined}>
      {() => <LeadSourceField phone={phone} />}
    </Field>
  )

  const budgetOptions = BUDGET_BANDS.map((band: BudgetBand) => ({ value: band.key, label: band.label }))
  const budget = (
    <Field label="Budget">
      {(ids) => (phone
        ? (
          /* *Not sure* is the box's placeholder, grey, not a choice somebody made (Board 5). */
          <PhonePicker label="Budget" value={job.budgetKey === 'not_sure' ? null : job.budgetKey}
            options={budgetOptions} placeholder="Not sure"
            onChange={(value) => setJob({ budgetKey: value ?? 'not_sure' })} />
        )
        : (
          <KeySelect id={ids.id} label="Budget"
            value={job.budgetKey === 'not_sure' ? null : job.budgetKey}
            placeholder="Not sure" options={budgetOptions}
            onChange={(value) => setJob({ budgetKey: value ?? 'not_sure' })} />
        ))}
    </Field>
  )

  const when = (
    <Field label="When they want it done" group>
      {() => <WhenField phone={phone} />}
    </Field>
  )

  return (
    <section aria-label="Job">
      <SectionHeading section="job" ready={ready} />
      {phone
        ? (
          <div className="flex flex-col gap-2.5">
            {title}
            {divisionField}
            {leadSource}
            {when}
            {budget}
          </div>
        )
        : (
          <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
            {title}
            {divisionField}
            {leadSource}
            {budget}
            <div className="col-span-2">{when}</div>
          </div>
        )}
    </section>
  )
}

/**
 * Lead source: the company's sources, most used first, and *Add “<text>” as a
 * lead source* for one that isn't there (Board 6 A). A new one is only a
 * name until Create, which adds it (§11.2 step 4).
 */
function LeadSourceField({ phone }: { phone: boolean }) {
  const api = useRequestSheet()
  const { setup } = api
  const job = api.state.job
  const name = leadSourceName(job, setup)

  const pick = (id: string | null) => api.update((state) => ({
    ...state, job: { ...state.job, leadSourceId: id, newLeadSource: null, leadSourceFromHistory: false },
  }))
  const add = (text: string) => {
    const same = setup.leadSources.find((source) => source.name.toLowerCase() === text.toLowerCase())
    if (same) { pick(same.id); return }
    api.update((state) => ({
      ...state, job: { ...state.job, leadSourceId: null, newLeadSource: text, leadSourceFromHistory: false },
    }))
  }
  const addWords = (text: string) => `Add “${text}” as a lead source`

  if (phone) {
    return (
      <PhonePicker
        label="Lead source"
        value={job.leadSourceId}
        display={job.newLeadSource}
        options={setup.leadSources.map((source) => ({
          value: source.id, label: source.name, detail: String(source.jobs),
        }))}
        placeholder="Choose one"
        onChange={pick}
        search
        onAdd={add}
        addWords={addWords}
      />
    )
  }
  return (
    <KeySelect
      label="Lead source"
      value={job.leadSourceId}
      display={job.newLeadSource ?? (job.leadSourceId ? null : name)}
      options={setup.leadSources.map((source) => ({
        value: source.id, label: source.name, detail: String(source.jobs),
      }))}
      placeholder="Choose one"
      onChange={pick}
      onAdd={add}
      addWords={addWords}
    />
  )
}

